<template>
  <div class="page" :style="themeStyle">
    <div v-if="!loaded" class="loading">Loading</div>
    <div v-if="loaded" class="root">

      <!-- Organic blob BG -->
      <div class="blob-bg" aria-hidden="true">
        <div class="blob b1"></div>
        <div class="blob b2"></div>
        <div class="blob b3"></div>
      </div>

      <!-- Nav -->
      <nav class="sage-nav">
        <img v-if="loinfo && loinfo.main_logo" :src="loinfo.main_logo" class="sn-logo" />
        <div class="sn-pair">
          <span class="sn-name">{{ loName }}</span>
          <span class="sn-amp">&</span>
          <span class="sn-re">{{ realtorName }}</span>
        </div>
  
      </nav>

      <!-- Main content area -->
      <div class="sage-content">

        <!-- Hero text left -->
        <section class="sage-hero">
          <div class="sh-tag">
            <div class="tag-leaf">✦</div>
            <span>Home Lending</span>
          </div>
          <h1 class="sh-h1" v-html="heroH1Html"></h1>
          <p class="sh-body">{{ heroBody }}</p>
          <div class="sh-btns">
            <a class="sh-btn-fill" :href="applyRightLink">{{ heroPrimaryText }}</a>
            <a v-if="applyLeftLink" class="sh-btn-out" :href="applyLeftLink">{{ heroSecondaryText }}</a>
          </div>
          <div class="sh-chips">
            <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="sh-chip">{{ loinfo.phone_number }}</a>
            <a v-if="loinfo && loinfo.email" :href="'mailto:' + loinfo.email" class="sh-chip">Email Us</a>
            <a v-if="realtor && realtor.website_url" :href="realtor.website_url" class="sh-chip" target="_blank" rel="noreferrer">Partner Site</a>
          </div>
        </section>

        <!-- Cards -->
        <aside class="sage-cards">

          <div class="sage-card lo-sage">
            <div class="scard-arch"></div>
            <div class="scard-inner">
              <div class="scard-badge lo-badge">Loan Officer</div>
              <img v-if="loinfo && loinfo.lo_image" :src="loinfo.lo_image" class="scard-photo" />
              <div class="scard-name">{{ loName }}</div>
              <div class="scard-role">{{ loTitle }}</div>
              <div v-if="loinfo && loinfo.nmls" class="scard-nmls">NMLS {{ (loinfo.nmls || "").trim() }}</div>
              <div class="scard-sep lo-scard-sep"></div>
              <div class="scard-contacts">
                <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="sc-link lo-link">{{ loinfo.phone_number }}</a>
                <a v-if="loinfo && loinfo.email" :href="'mailto:' + loinfo.email" class="sc-link lo-link">{{ loinfo.email }}</a>
              </div>
      
            </div>
          </div>

          <div class="sage-card re-sage">
            <div class="scard-arch re-arch"></div>
            <div class="scard-inner">
              <div class="scard-badge re-badge">{{ realtor?.partner_title || 'Partner' }}</div>
              <img v-if="realtor && realtor.headshot_url" :src="realtor.headshot_url" class="scard-photo" />
              <div class="scard-name">{{ realtorName }}</div>
              <div class="scard-role">{{ realtor && realtor.brokerage_name }}</div>
              <div v-if="realtor && realtor.nmls" class="scard-nmls">NMLS {{ (realtor.nmls || "").trim() }}</div>
              <div class="scard-sep re-scard-sep"></div>
              <div class="scard-contacts">
                <a v-if="realtor && realtor.phone_number" :href="'tel:' + realtor.phone_number" class="sc-link re-link">{{ realtor.phone_number }}</a>
                <a v-if="realtor && realtor.email" :href="'mailto:' + realtor.email" class="sc-link re-link">{{ realtor.email }}</a>
                <a v-if="realtor && realtor.website_url" :href="realtor.website_url" class="sc-link re-link" target="_blank" rel="noreferrer">Website →</a>
              </div>
            </div>
          </div>

        </aside>
      </div>

    </div>
  </div>
</template>

<script>
import coBrandMixin from "../../mixins/coBrandMixin.js";
const BRAND_COLORS = {
  zonalending: { primary: "#eb610a", accent: "#646363" },
  radiantmortgage: { primary: "#1C1F39", accent: "#DB9457" },
  conradmortgage: { primary: "#4a4b4b", accent: "#d4af37" },
  theimgteam: { primary: "#ff6633", accent: "#4a4c54" },
  responsivemortgage: { primary: "#07212c", accent: "#FF7F47" },
  kpmortgage: { primary: "#0D2821", accent: "#34BFC6" },
  ignitemortgage: { primary: "#000000", accent: "#00b1eb" },
  goldstarfinancial: { primary: "#0b1726", accent: "#c69c39" },
};
function getBrandKey(email) {
  if (!email) return null;
  return email.slice(email.indexOf("@") + 1).toLowerCase().split(".")[0] || null;
}
export default {
  name: "CoBrandSage",
  mixins: [coBrandMixin],
  computed: {
    brandKey() { const k = getBrandKey(this.loinfo?.email); return (k && BRAND_COLORS[k]) ? k : "goldstarfinancial"; },
    palette() { return BRAND_COLORS[this.brandKey] || BRAND_COLORS.goldstarfinancial; },
    themeStyle() { return { "--p": this.palette.primary, "--a": this.palette.accent }; },
    heroH1Html() {
      const h = (this.realtor?.header1 || this.loinfo?.header1 || "").trim();
      if (!h) return "Grow your roots,<br/><em>find home.</em>";
      const parts = h.split(",");
      if (parts.length > 1) return `${parts[0].trim()}<br/><em>${parts.slice(1).join(",").trim()}</em>`;
      return h;
    },
    heroPrimaryText() { return this.loinfo?.right_button_text || "Apply Now"; },
    heroSecondaryText() { return this.loinfo?.left_button_text || "Schedule Appointment"; },
    heroBody() {
      const m = (this.realtor?.main || this.loinfo?.main || "").trim();
      if (m) return m;
      return [this.loinfo?.text_section_body_p1, this.loinfo?.text_section_body_p2].filter(Boolean).join(" ").trim()
        || this.loinfo?.footer_slogan || "Start your application in minutes and we'll guide you from application through closing.";
    },
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,600;1,400&family=Mulish:wght@300;400;500;600&display=swap');

.page {
  min-height: 100vh;
  background: #f2f5ef;
  color: #1e2318;
  font-family: 'Mulish', sans-serif;
  --p: #0b1726;
  --a: #c69c39;
  --bg: #f2f5ef;
  --sage: #6b8c6e;
  --card-bg: #ffffff;
  --border: rgba(30,35,24,0.1);
  --muted: #6b7060;
  overflow: hidden;
  position: relative;
}
.root { position: relative; z-index: 1; display: flex; flex-direction: column; min-height: 100vh; }

/* BLOB BG */
.blob-bg { position: fixed; inset: 0; pointer-events: none; z-index: 0; overflow: hidden; }
.blob { position: absolute; border-radius: 50%; filter: blur(60px); opacity: 0.2; }
.b1 { width: 400px; height: 350px; background: color-mix(in srgb, var(--sage) 40%, var(--bg)); top: -80px; right: 10%; }
.b2 { width: 350px; height: 300px; background: color-mix(in srgb, var(--a) 25%, var(--bg)); bottom: 10%; left: -5%; }
.b3 { width: 250px; height: 250px; background: color-mix(in srgb, var(--p) 12%, var(--bg)); top: 40%; right: -5%; }

/* NAV */
.sage-nav { position: relative; z-index: 10; display: flex; align-items: center; justify-content: space-between; padding: 22px 52px; }
.sn-logo { width: 145px; max-height:74px; object-fit: contain; }
.sn-pair { display: flex; align-items: center; gap: 10px; font-size: 12px; color: var(--muted); }
.sn-name { font-weight: 500; color: var(--p); }
.sn-amp { color: var(--sage); }
.sn-cta { font-size: 12px; font-weight: 600; text-decoration: none; background: var(--p); color: #fff; padding: 10px 22px; border-radius: 24px; transition: opacity 0.2s; }
.sn-cta:hover { opacity: 0.88; }

/* CONTENT */
.sage-content { position: relative; z-index: 1; display: grid; grid-template-columns: 1fr 380px; max-width: 1180px; margin: 0 auto; width: 100%; padding: 40px 52px 60px; gap: 52px; align-items: center; }

/* HERO */
.sage-hero { }
.sh-tag { display: flex; align-items: center; gap: 8px; margin-bottom: 20px; font-size: 11px; font-weight: 500; letter-spacing: 0.14em; text-transform: uppercase; color: var(--sage); }
.tag-leaf { font-size: 9px; }
.sh-h1 { font-family: 'Lora', serif; font-size: clamp(36px, 4.8vw, 60px); font-weight: 600; line-height: 1.1; color: var(--p); margin: 0 0 20px; }
.sh-h1 em { font-style: italic; color: color-mix(in srgb, var(--sage) 70%, var(--p)); }
.sh-body { font-size: 15px; line-height: 1.8; color: var(--muted); margin: 32px auto auto auto; font-weight: 300; max-width: 480px; }
.sh-btns { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 24px; justify-content: center;margin-top:20px }
.sh-btn-fill { font-size: 13px; font-weight: 600; text-decoration: none; background: var(--p); color: #fff; padding: 13px 28px; border-radius: 8px; transition: opacity 0.2s; width: 55%; text-align: center; }
.sh-btn-fill:hover { opacity: 0.88; }
.sh-btn-out { font-size: 13px; font-weight: 400; text-decoration: none; color: var(--muted); border: 1.5px solid var(--border); padding: 13px 22px; border-radius: 8px; background: rgba(255,255,255,0.5); transition: background 0.18s; }
.sh-btn-out:hover { background: rgba(255,255,255,0.9); }
.sh-chips { display: flex; gap: 8px; flex-wrap: wrap; }
.sh-chip { font-size: 11px; text-decoration: none; color: var(--sage); background: rgba(107,140,110,0.1); border: 1px solid rgba(107,140,110,0.2); padding: 6px 14px; border-radius: 20px; transition: background 0.18s; }
.sh-chip:hover { background: rgba(107,140,110,0.2); }

/* SAGE CARDS */
.sage-cards { display: flex; flex-direction: column; gap: 18px; }
.sage-card { background: var(--card-bg); border-radius: 20px; overflow: hidden; box-shadow: 0 4px 24px rgba(30,35,24,0.08); border: 1px solid var(--border); transition: transform 0.2s; }
.sage-card:hover { transform: translateY(-3px); }
.scard-arch { height: 6px; }
.lo-sage .scard-arch { background: linear-gradient(90deg, var(--p), color-mix(in srgb, var(--p) 50%, var(--sage))); }
.re-arch { background: linear-gradient(90deg, var(--a), color-mix(in srgb, var(--a) 50%, var(--sage))); }
.scard-inner { padding: 22px; text-align: center; display: flex; flex-direction: column; align-items: center; }
.scard-badge { font-size: 9px; font-weight: 600; letter-spacing: 0.18em; text-transform: uppercase; margin-bottom: 14px; padding: 4px 12px; border-radius: 20px; }
.lo-badge { color: var(--p); background: color-mix(in srgb, var(--p) 8%, transparent); border: 1px solid color-mix(in srgb, var(--p) 15%, transparent); }
.re-badge { color: color-mix(in srgb, var(--a) 80%, #1e2318); background: color-mix(in srgb, var(--a) 10%, transparent); border: 1px solid color-mix(in srgb, var(--a) 20%, transparent); }
.scard-photo { width: 68px; border-radius: 50%; object-fit: cover; border: 3px solid white; box-shadow: 0 2px 12px rgba(0,0,0,0.12); margin-bottom: 12px; }
.scard-name { font-family: 'Lora', serif; font-size: 18px; font-weight: 600; color: var(--p); margin-bottom: 2px; }
.scard-role { font-size: 11px; color: var(--muted); }
.scard-nmls { font-size: 10px; color: color-mix(in srgb, var(--muted) 60%, transparent); margin-top: 2px; }
.scard-sep { height: 1px; width: 100%; margin: 14px 0; }
.lo-scard-sep { background: color-mix(in srgb, var(--p) 10%, transparent); }
.re-scard-sep { background: color-mix(in srgb, var(--a) 15%, transparent); }
.scard-contacts { display: flex; flex-direction: column; gap: 4px; margin-bottom: 14px; width: 100%; }
.sc-link { font-size: 11px; text-decoration: none; color: var(--muted); transition: color 0.18s; text-align: center; }
.lo-link:hover { color: var(--p); }
.re-link:hover { color: color-mix(in srgb, var(--a) 80%, #1e2318); }
.scard-cta { display: block; width: 100%; box-sizing: border-box; text-align: center; text-decoration: none; font-size: 12px; font-weight: 600; background: var(--p); color: #fff; padding: 11px; border-radius: 10px; transition: opacity 0.2s; }
.scard-cta:hover { opacity: 0.88; }

@media (max-width: 820px) {
  .sage-content { grid-template-columns: 1fr; padding: 32px 24px; }
  .sage-nav { padding: 16px 24px; }
  .sn-pair { display: none; }
}
</style>
